import React from 'react';
import { Flex, Box } from 'rebass';
import { Formik, FormikConfig } from 'formik';
import * as Yup from 'yup';
import { useDebounce } from 'use-debounce';

import {
  ButtonLarge,
  TermsAndConditions,
  FormHeader,
  FormSubHeader,
  ErrorNotification,
  ActionText,
  GraduationYearField,
  SpecialtyIdSelect,
  MedicalSchoolFieldChildrenProps,
  MedicalSchoolMenuItem,
  MedicalSchoolField,
} from '@mdlinx/component-library';
import { extractDataFromResponse } from '@mdlinx/frontend-shared';

import { useSearchMedicalSchoolsQuery } from 'generated/client';
import { FormContainer } from 'app/components/layout/form-container';

const MedicalSchoolMenu = ({ dropDownProps }: { dropDownProps: MedicalSchoolFieldChildrenProps }) => {
  const [nameSearchTerm] = useDebounce(dropDownProps.inputValue, 500);
  const searchMedicalSchoolsResponse = useSearchMedicalSchoolsQuery({
    variables: { input: { nameSearchTerm } },
  });
  if (searchMedicalSchoolsResponse.error) {
    console.error(searchMedicalSchoolsResponse.error);
    return null;
  }

  if (searchMedicalSchoolsResponse.loading) {
    return (
      <MedicalSchoolMenuItem
        suggestion={{ name: 'loading' }}
        index={0}
        itemProps={dropDownProps.getItemProps({ item: 'loading' })}
        highlightedIndex={dropDownProps.highlightedIndex}
        selectedItem={dropDownProps.selectedItem}
      />
    );
  }

  const { searchMedicalSchools } = extractDataFromResponse(searchMedicalSchoolsResponse);

  switch (searchMedicalSchools.__typename) {
    case 'SearchMedicalSchoolsSuccess':
      return (
        <>
          {searchMedicalSchools.medicalSchools.map((medicalSchool, index) => (
            <MedicalSchoolMenuItem
              key={`${medicalSchool.name}-${index}`}
              suggestion={medicalSchool}
              index={index}
              itemProps={dropDownProps.getItemProps({ index, item: medicalSchool.name })}
              highlightedIndex={dropDownProps.highlightedIndex}
              selectedItem={dropDownProps.selectedItem}
            />
          ))}
        </>
      );
    case 'SearchMedicalSchoolsFailure':
      return null;
    default:
      throw new Error(`__typename should be specified. ${JSON.stringify({ searchMedicalSchools })}`);
  }
};

export type SignUpFormAdditionalInfoProps = {
  onSubmit: FormikConfig<{
    medicalSchool: string;
    graduationYear: string;
    specialtyId: string;
  }>['onSubmit'];
  errorMessage?: string;
  onDeclineSubmitMedicalSchool: () => void;
};

export const SignUpFormAdditionalInfo = ({
  onSubmit,
  errorMessage,
  onDeclineSubmitMedicalSchool,
}: SignUpFormAdditionalInfoProps) => {
  return (
    <FormContainer>
      <FormHeader>You are now a MDLinx member</FormHeader>
      <Box mt="10px" />
      <FormSubHeader>
        However we were unable to verify your status as a physician. If you would like to update your credentials to
        personalize your experience please provide the following:
      </FormSubHeader>
      <Box mt="10px" />
      {errorMessage && (
        <Box my="12px">
          <ErrorNotification errorMessage={errorMessage} />
        </Box>
      )}
      <Formik
        initialValues={{
          medicalSchool: '',
          graduationYear: '',
          specialtyId: '',
        }}
        validationSchema={Yup.object({
          medicalSchool: Yup.string().required('Required'),
          graduationYear: Yup.number()
            .moreThan(1900, 'Invalid year')
            .lessThan(2100, 'Invalid year')
            .required('Required'),
          specialtyId: Yup.number().required('Required'),
        })}
        onSubmit={onSubmit}
      >
        {formik => (
          <form onSubmit={formik.handleSubmit}>
            <MedicalSchoolField>
              {(dropDownProps: MedicalSchoolFieldChildrenProps) => {
                return <MedicalSchoolMenu dropDownProps={dropDownProps} />;
              }}
            </MedicalSchoolField>

            <GraduationYearField />

            <SpecialtyIdSelect />

            <Box mt="5px" />

            <ButtonLarge type="submit" disabled={!(formik.dirty && formik.isValid)} isLoading={formik.isSubmitting}>
              Update Account
            </ButtonLarge>
            <Box mt="30px" />
            <ActionText onClick={onDeclineSubmitMedicalSchool}>Continue as a non-physician</ActionText>
          </form>
        )}
      </Formik>
      <Box mt="20px" />
      <Flex justifyContent="center">
        <TermsAndConditions />
      </Flex>
    </FormContainer>
  );
};
